import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useList } from "@refinedev/core";
import { FileText, Plus } from "lucide-react";
import { LoadingScreen } from "../common/LoadingScreen";
import { useAuth } from "../../contexts/AuthContext";
import { supabaseClient } from "../../lib/supabase";

interface ProjectUser {
  user_id: string;
  role: string;
}

interface Project {
  id: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  owner_id: string;
  project_users?: ProjectUser[];
}

export function ProjectList() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [directProjects, setDirectProjects] = useState<Project[]>([]);
  const [loadingDirect, setLoadingDirect] = useState(false);
  const { user } = useAuth();

  // Get user role
  const userRole = 
    user?.user_metadata?.role || 
    user?.role ||
    (user?.email === 'andmaillev@gmail.com' ? 'admin' : 'client');

  // Only admins and optimizers can create projects
  const canCreateProject = userRole === 'admin' || userRole === 'optimizer' || user?.email === 'andmaillev@gmail.com';

  // Direct loading from Supabase
  useEffect(() => {
    const loadProjectsDirect = async () => {
      if (!user) return;
      
      setLoadingDirect(true);
      try {
        const { data, error } = await supabaseClient
          .from('projects')
          .select(`
            *,
            project_users (
              user_id,
              role
            )
          `)
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        console.log('Direct Supabase load - all projects:', data?.length);
        console.log('Direct projects data:', data);
        setDirectProjects(data || []);
      } catch (error) {
        console.error('Error loading projects directly:', error);
      } finally {
        setLoadingDirect(false);
      }
    };

    loadProjectsDirect();
  }, [user]);

  const { data, isLoading } = useList<Project>({
    resource: "projects",
    pagination: {
      mode: "off"
    },
    sorters: [
      {
        field: "created_at",
        order: "desc"
      }
    ],
    meta: {
      select: `
        *,
        project_users (
          user_id,
          role
        )
      `
    }
  });

  const projects = data?.data || [];
  
  console.log('ProjectList Debug:');
  console.log('User:', user?.email, 'Role:', userRole);
  console.log('Refine projects loaded:', projects.length);
  console.log('Direct projects loaded:', directProjects.length);
  console.log('Using direct projects for comparison');
  
  // Use direct projects for now to debug
  const projectsToUse = directProjects;

  // Filter projects based on user role and access
  const filteredProjects = projectsToUse.filter(project => {
    // Admins can see all projects
    if (userRole === 'admin' || user?.email === 'andmaillev@gmail.com') {
      return true;
    }

    // Optimizers can see projects they created or where they are assigned
    if (userRole === 'optimizer') {
      return project.owner_id === user?.id || 
             project.project_users?.some(pu => 
               pu.user_id === user?.id && pu.role === 'optimizer'
             );
    }

    // Clients can only see projects where they are assigned
    if (userRole === 'client') {
      return project.project_users?.some(pu => 
        pu.user_id === user?.id
      );
    }

    return false;
  });

  console.log('Filtered projects:', filteredProjects.length);
  console.log('Filtered projects data:', filteredProjects);

  // Filter by search term
  const searchedProjects = filteredProjects.filter(project => 
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading || loadingDirect) return <LoadingScreen />;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Projects</h2>
          {canCreateProject && (
            <button
              onClick={() => navigate("/projects/create")}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Project
            </button>
          )}
        </div>

        <div className="mb-4">
          <input
            type="text"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {searchedProjects.map((project) => {
            // Check if user can edit this project
            const canEdit = userRole === 'admin' || 
                          userRole === 'optimizer' && (
                            project.owner_id === user?.id ||
                            project.project_users?.some(pu => 
                              pu.user_id === user?.id && pu.role === 'optimizer'
                            )
                          );

            return (
              <div
                key={project.id}
                className="border rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {project.name}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                      {project.description || "No description"}
                    </p>
                  </div>
                  <FileText className="h-5 w-5 text-gray-400" />
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <div className="flex space-x-2">
                    {canEdit && (
                      <button
                        onClick={() => navigate(`/projects/edit/${project.id}`)}
                        className="text-sm text-indigo-600 hover:text-indigo-900"
                      >
                        Edit
                      </button>
                    )}
                    <button
                      onClick={() => navigate(`/projects/show/${project.id}`)}
                      className="text-sm px-4 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                    >
                      Open
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {searchedProjects.length === 0 && (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No projects</h3>
            {canCreateProject ? (
              <>
                <p className="mt-1 text-sm text-gray-500">
                  Get started by creating a new project.
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => navigate("/projects/create")}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Project
                  </button>
                </div>
              </>
            ) : (
              <p className="mt-1 text-sm text-gray-500">
                No projects are assigned to you yet.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}